/**
 * Citations in the Full response reader — `[N]` / `[LN]` markers made
 * clickable the way the section editor makes them clickable.
 *
 * Citation numbers restart in every draft segment, and a section's html joins
 * several segments, so a marker means nothing without the segment it sits in.
 * The document API wraps each segment's part in `<div data-segment-id="…">`
 * and sends that segment's refs beside it (`section.segments`). Here each
 * marker is resolved against its own wrapper's refs and wrapped in the span the
 * editor's citationDecorationPlugin paints (`citation-badge-inline`, with the
 * same data attributes), so the reader's click handler can open the editor's
 * CitationTooltip.
 *
 * - A marker with no ref in its segment stays plain text.
 * - The wrappers are removed once read, so the prose renders exactly as it did
 *   before they existed (Tailwind Typography styles `.prose > :first-child`).
 * - Runs on the RAW html, before `sanitizeResponseHtml`, so DOMPurify stays the
 *   last thing to touch the markup. DOMParser documents are inert: nothing in
 *   them runs or loads.
 */

import { CITATION_MARKER_PATTERNS } from '@/components/drafting/citationDecorationPlugin'
import type { CitationRef, FullResponseSection, FullResponseSegment } from '@/lib/api'
import type { CitingContent } from '@/lib/draft-stats'

const SEGMENT_ATTRIBUTE = 'data-segment-id'

type CitationKind = 'tender' | 'library'

interface Marker {
  start: number
  end: number
  kind: CitationKind
  form: 'marker' | 'loose'
  ref: CitationRef | undefined
}

// ===================
// Parsing
// ===================

function parseBody(html: string): HTMLElement {
  return new DOMParser().parseFromString(html, 'text/html').body
}

/** The top-level segment wrappers, in document order. */
function segmentWrappers(body: HTMLElement): HTMLElement[] {
  return Array.from(body.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && el.hasAttribute(SEGMENT_ATTRIBUTE),
  )
}

function refFor(segment: FullResponseSegment, kind: CitationKind, index: number): CitationRef | undefined {
  const refs = kind === 'library' ? segment.libraryCitationRefs : segment.citationRefs
  return refs.find((ref) => ref.citationIndex === index)
}

/**
 * Every marker in a text node, the editor's way: strict shapes first, and a
 * later match that overlaps an earlier one is skipped — whether or not the
 * earlier one resolved, so an unresolved `[1]` is never re-read as a loose
 * form.
 */
function findMarkers(text: string, segment: FullResponseSegment): Marker[] {
  const markers: Marker[] = []
  for (const { regex, kind, form } of CITATION_MARKER_PATTERNS) {
    // matchAll copies the shared /g regex, so its lastIndex is never touched.
    for (const match of text.matchAll(regex)) {
      const index = parseInt(match[1] ?? '', 10)
      if (!(index >= 1)) continue
      const start = match.index ?? 0
      const end = start + match[0].length
      if (markers.some((m) => start < m.end && end > m.start)) continue
      markers.push({ start, end, kind, form, ref: refFor(segment, kind, index) })
    }
  }
  return markers.filter((m) => m.ref !== undefined).sort((a, b) => a.start - b.start)
}

function badgeFor(doc: Document, text: string, marker: Marker, segmentId: string): HTMLSpanElement {
  const ref = marker.ref!
  const badge = doc.createElement('span')
  badge.className = 'citation-badge-inline'
  badge.setAttribute('data-citation-index', String(ref.citationIndex))
  badge.setAttribute('data-section-id', segmentId)
  badge.setAttribute('data-citation-form', marker.form)
  if (marker.kind === 'library') badge.setAttribute('data-citation-type', 'library')
  // The editor's badge is mouse-only; here it is also reachable by keyboard.
  badge.setAttribute('role', 'button')
  badge.setAttribute('tabindex', '0')
  badge.setAttribute('aria-label', `Source: ${ref.documentName}`)
  badge.textContent = text
  return badge
}

function decorateTextNode(node: Text, segment: FullResponseSegment): void {
  const text = node.data
  const markers = findMarkers(text, segment)
  if (markers.length === 0) return

  const doc = node.ownerDocument
  const fragment = doc.createDocumentFragment()
  let cursor = 0
  for (const marker of markers) {
    if (marker.start > cursor) fragment.append(text.slice(cursor, marker.start))
    fragment.append(badgeFor(doc, text.slice(marker.start, marker.end), marker, segment.segmentId))
    cursor = marker.end
  }
  if (cursor < text.length) fragment.append(text.slice(cursor))
  node.replaceWith(fragment)
}

function decorateWithin(wrapper: HTMLElement, segment: FullResponseSegment): void {
  const walker = wrapper.ownerDocument.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT)
  // Collect first: replacing a node mid-walk would derail the walker.
  const textNodes: Text[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text)
  }
  textNodes.forEach((node) => decorateTextNode(node, segment))
}

// ===================
// Public API
// ===================

/**
 * Wrap every citation marker that has a ref in its own segment in the editor's
 * citation badge, and remove the segment wrappers. Html without wrappers (an
 * older backend) comes back unchanged.
 */
export function decorateCitations(html: string, segments: readonly FullResponseSegment[] | undefined): string {
  if (!html || !html.includes(SEGMENT_ATTRIBUTE)) return html
  const body = parseBody(html)
  const bySegmentId = new Map((segments ?? []).map((segment) => [segment.segmentId, segment]))
  for (const wrapper of segmentWrappers(body)) {
    const segment = bySegmentId.get(wrapper.getAttribute(SEGMENT_ATTRIBUTE) ?? '')
    if (segment) decorateWithin(wrapper, segment)
    wrapper.replaceWith(...Array.from(wrapper.childNodes))
  }
  return body.innerHTML
}

/**
 * A section's segments as `collectCitedSources` reads them: each segment's own
 * html beside its own refs, so a ref counts only while its marker is still in
 * that segment's text. `null` when the backend sent no segments — the page
 * then cannot know its sources.
 */
export function citingParts(section: Pick<FullResponseSection, 'html' | 'segments'>): CitingContent[] | null {
  if (!section.segments) return null
  if (!section.html) return []
  const contentById = new Map(
    segmentWrappers(parseBody(section.html)).map((wrapper) => [
      wrapper.getAttribute(SEGMENT_ATTRIBUTE) ?? '',
      wrapper.innerHTML,
    ]),
  )
  return section.segments.map((segment) => ({
    content: contentById.get(segment.segmentId) ?? '',
    citationRefs: segment.citationRefs,
    libraryCitationRefs: segment.libraryCitationRefs,
  }))
}
